/**
 * The desktop-handoff stash (sdk ADR-011) — the
 * `{returnUrl, nonce}` pair a `/desktop-auth` leg leaves for its SECOND tab,
 * claim-once across tabs. Lives in SDK storage so the sign-out gate reclaims
 * it: an abandoned handoff must not survive into the next account's shell.
 * No expiry: an abandoned attempt never produced a session.
 */
import { SDK_STORAGE_NAMESPACE } from '../storage/types.js';
import type { SDKStorage } from '../storage/types.js';

export const DESKTOP_HANDOFF_KEY = `${SDK_STORAGE_NAMESPACE}desktop-handoff`;

export interface HandoffEntry {
  returnUrl: string;
  nonce: string;
}

/** Compose and stash the handoff pair. Throws on a malformed input — this
 *  module is the key's only writer. */
export function stashDesktopHandoff(
  storage: SDKStorage,
  entry: HandoffEntry,
): void {
  if (!entry.returnUrl || !entry.nonce) {
    throw new Error('desktop-handoff: returnUrl and nonce are required');
  }
  storage.setItem(DESKTOP_HANDOFF_KEY, JSON.stringify(entry));
}

/** Cross-tab single-flight claim: read-and-remove. Returns null when no
 *  handoff is stashed. */
export function claimDesktopHandoff(storage: SDKStorage): HandoffEntry | null {
  const raw = storage.getItem(DESKTOP_HANDOFF_KEY);
  if (raw === null) return null;
  storage.removeItem(DESKTOP_HANDOFF_KEY);
  const parsed: unknown = JSON.parse(raw);
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    typeof (parsed as HandoffEntry).returnUrl !== 'string' ||
    typeof (parsed as HandoffEntry).nonce !== 'string'
  ) {
    throw new Error(`desktop-handoff at ${DESKTOP_HANDOFF_KEY} is not a {returnUrl, nonce} pair`);
  }
  return parsed as HandoffEntry;
}

/** Remove without claiming (the sign-out gate's reset). */
export function clearDesktopHandoff(storage: SDKStorage): void {
  storage.removeItem(DESKTOP_HANDOFF_KEY);
}
